import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import toast from "react-hot-toast";
import * as yup from "yup";
import TextInput from "../../../../components/form/text-input";
import Modal from "../../../../components/modal";
import Button from "../../../../components/new-button";
import baseStyles from "../base.module.scss";
import { saveEquipmentBrand } from "../../../../../api";
import config from "../../../../../config";
import ImageCropper from "../../../../components/image-cropper";
import ImageUpload from "../../../../components/image-upload";
import { fetchBrands } from "../../../../stores/equipment";

const STRATUS_URL = config.paymentcloud.STRATUS_URL;

const schema = yup.object({
  brand_name: yup.string().trim().required("* Brand name is required"),
});

export const EquipmentBrandModal = ({
  open,
  onClose,
  isEdit = false,
  activeBrand = null,
}) => {
  const [imageCropDialogOpen, setImageCropDialogOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageToShow, setImageToShow] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);

  const getInitialValues = () => {
    if (isEdit && activeBrand) {
      return {
        brand_name: activeBrand.name || "",
        brand_image: null,
        description: activeBrand.description || "",
      };
    }

    return {
      brand_name: "",
      brand_image: null,
      description: "",
    };
  };

  const handleImageCrop = (file) => {
    setImageCropDialogOpen(true);
    setUploadedImage(URL.createObjectURL(file));
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    toast.loading(`${isEdit ? "Updating" : "Creating"} brand...`);

    try {
      const formData = new FormData();
      if (croppedImageBlob) {
        formData.append("brand_image", croppedImageBlob);
      }
      formData.append("brand_name", values.brand_name);
      formData.append("description", values.description);
      if (isEdit && activeBrand) {
        formData.append("id", activeBrand.id);
      }

      const response = await saveEquipmentBrand(formData);
      toast.dismiss();

      if (response.status) {
        setSubmitting(false);
        toast.success(`Brand ${isEdit ? "updated" : "created"} successfully`);
        fetchBrands();
        onClose();
      } else {
        setSubmitting(false);
        toast.error(
          response.message ||
            `There was a problem ${isEdit ? "updating" : "creating"} brand`,
        );
      }
    } catch (error) {
      setSubmitting(false);
      toast.error(
        `There was a problem ${isEdit ? "updating" : "creating"} brand`,
      );
      console.error(`Error ${isEdit ? "updating" : "creating"} brand:`, error);
    }
  };

  useEffect(() => {
    if (isEdit && activeBrand) {
      setImageToShow(null);
      setCroppedImageBlob(null);
    }
  }, [activeBrand, isEdit]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      header={`${isEdit ? "Edit" : "Add"} brand`}
      notCentered
      size="sm"
    >
      <Formik
        enableReinitialize
        validationSchema={schema}
        onSubmit={handleFormSubmit}
        initialValues={getInitialValues()}
        validateOnBlur={false}
        validateOnChange={true}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
              <div
                className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
              >
                <TextInput
                  label="Name"
                  id="brand_name"
                  name="brand_name"
                  placeholder="Enter brand name"
                  value={values.brand_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched.brand_name}
                  errored={errors.brand_name}
                  error={errors.brand_name}
                />

                <div>
                  <label className={baseStyles.label}>Image</label>
                  <ImageUpload
                    onImageSelect={(file) => {
                      handleImageCrop(file);
                    }}
                    onImageRemove={() => {
                      setCroppedImageBlob(null);
                      setImageToShow(null);
                    }}
                    currentImage={
                      imageToShow ||
                      (isEdit && activeBrand
                        ? `/get-image-by-path?path=${activeBrand.image}`
                        : null)
                    }
                    placeholder="Upload Photo by drag and drop or click to upload"
                    subText="high quality image"
                    accept="image/*"
                    size="medium"
                  />
                </div>
              </div>

              <div>
                <label className={baseStyles.label}>Description</label>
                <TextInput
                  name="description"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter brand description"
                  value={values.description}
                  touched={touched.description}
                  errored={errors.description}
                  error={errors.description}
                />
              </div>
            </div>

            <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
              <div
                className={`${baseStyles.flex} ${baseStyles.flexEnd} ${baseStyles.gap1}`}
                style={{ marginTop: "16px" }}
              >
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isEdit ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>

      <ImageCropper
        open={imageCropDialogOpen}
        image={uploadedImage}
        onComplete={(croppedImage) => {
          fetch(croppedImage)
            .then((response) => response.blob())
            .then((blob) => {
              setCroppedImageBlob(blob);
              setImageToShow(croppedImage);
              setImageCropDialogOpen(false);
            });
        }}
        onClose={() => setImageCropDialogOpen(false)}
        aspect={1}
      />
    </Modal>
  );
};

export default EquipmentBrandModal;
